import { defineStore } from 'pinia';
import {
  GetTestProfilesRequest,
  GetTestsRequest,
  TestProfileRow,
  TetsType,
} from 'src/modules/dataset/types/testProfile';
import { useApiStore } from 'src/core/stores/api';
import { useErrorHandling } from 'src/core/composables/useErrorHandling';

type TestProfilesStore = {
  test_profiles: TestProfileRow[];
  tests: TetsType[];
  isNewAvailable: boolean;
};

/**
 * Represents the test profile store.
 */
export const useTestProfilesStore = defineStore('test_profiles', {
  state: () =>
    ({
      test_profiles: [] as TestProfileRow[],
      tests: [],
      isNewAvailable: false,
    } as TestProfilesStore),

  getters: {
    // getters
  },

  actions: {
    /**
     * Retrieves the test profiles from the API and updates the store's test_profiles property.
     * @returns {Promise<void>} A promise that resolves when the test profiles are retrieved and updated.
     */
    async getTestProfiles(
      assignToStore = true,
      payload?: GetTestProfilesRequest
    ) {
      const apiStore = useApiStore();
      const { handleError } = useErrorHandling();
      try {
        const responseDatasets = await apiStore.service.post<TestProfileRow[]>(
          '/dataset/test_profile/read',
          payload
        );
        if (await apiStore.checkResponseSuccesful(responseDatasets)) {
          if (assignToStore) {
            this.test_profiles = responseDatasets.data;
          } else {
            return responseDatasets.data;
          }
        }
      } catch (error) {
        handleError('Errot getting test profiles', error);
        return [];
      }
    },
    async getTestsInfo() {
      const apiStore = useApiStore();
      const { handleError } = useErrorHandling();
      try {
        const responseTestsInfo = await apiStore.service.post(
          '/dataset/test_profile/info',
          {}
        );
        if (responseTestsInfo?.data) {
          this.isNewAvailable = responseTestsInfo.data.is_new_available;
        }
      } catch (error) {
        handleError('Errot getting tests INFO', error);
      }
    },
    /**
     * Retrieves tests based on the provided payload.
     * @param {GetTestsRequest} payload - The request payload for retrieving tests.
     */
    async getTests(
      payload: GetTestsRequest,
      assignToStore = true
    ): Promise<{ status: boolean; data?: TetsType[] }> {
      const apiStore = useApiStore();
      const { handleError } = useErrorHandling();
      try {
        const responseTestProfile = await apiStore.service.post<TetsType[]>(
          '/dataset/test_profile/test_list',
          payload
        );
        if (assignToStore) {
          this.tests = responseTestProfile.data;
          return { status: true };
        } else {
          return { status: true, data: responseTestProfile.data };
        }
      } catch (error) {
        handleError('Error getting tests', error);
        this.tests = [];
        return { status: false };
      }
    },
  },
});
